/**
 * B01 3D configurator glue for the product page — the part that needs a
 * product. Switches the product gallery between photos and the 3D pane
 * (from the button under the colour selector and the repurposed mobile zoom
 * bubble), forwards Shopify's own variant:change to the model's colour, and
 * reports the open/close of each 3D session. Only present on the
 * product.b01-3d template.
 *
 * Everything that works without a product — mounting, colour and material
 * handling, Maße, the whole AR chain, the QR modal and the ?ar=1 deep link —
 * lives in assets/b01-viewer-core.js, shared with the B2B page.
 */
(function () {
  'use strict';

  var gallery = document.querySelector('.product-gallery');
  var pane = gallery && gallery.querySelector('[data-viewer-pane]');
  if (!gallery || !pane || !window.BensbergerB01Core) return; // not the b01-3d template

  var controlsRow = document.querySelector('[data-viewer-controls]');
  var belowSelectorBtn = controlsRow && controlsRow.querySelector('[data-viewer-toggle-single]');
  var dimsToggleBtn = controlsRow && controlsRow.querySelector('[data-viewer-dims-toggle]');
  var arStartBtn = controlsRow && controlsRow.querySelector('[data-viewer-ar-start]');

  var qrModal = document.querySelector('[data-viewer-qr-modal]');

  var mobileToggleBtn = gallery.querySelector('[data-viewer-toggle-mobile]');

  var formId = gallery.getAttribute('form');
  var form = formId ? document.forms[formId] : null;

  // assets/b01-analytics.js defines this. The no-op fallback keeps every call
  // site below a plain one-liner even if that script failed to load, and
  // guarantees a reporting problem can never break the configurator itself.
  var track = window.B01Track || function () {};

  var mode = 'photo';
  var currentColorName = null;
  var currentVariant = null;

  // ---- initial color, from the variant already selected on page load ----
  (function readInitialColor() {
    var variantPicker = document.querySelector('variant-picker script[data-variant]');
    if (!variantPicker) return;
    try {
      var variant = JSON.parse(variantPicker.textContent);
      currentVariant = variant;
      currentColorName = variant && variant.option1;
    } catch (e) { /* ignore — falls back to whatever the viewer's own default is */ }
  })();

  var core = window.BensbergerB01Core.create({
    root: gallery,
    controls: controlsRow,
    qrModal: qrModal,
    initialColor: currentColorName,
    context: 'product',
    onDeepLink: function () { setMode('3d', 'qr_deeplink'); },
  });

  // ---- keep the photo gallery in step with colors picked in 3D ------------
  // The theme's own ProductGallery moves to a variant's featured media on
  // variant:change — but both of its paths need live layout: the carousel
  // branch is gated on `scrollWidth - clientWidth > 1`, and the grid branch
  // calls scrollIntoView. While the 3D pane is up, setMode has every photo
  // child `hidden`, so both measure zero and the move is silently dropped.
  // Switching back then only un-hides, leaving whatever slide was showing
  // before. So re-fire the event once the gallery is laid out again and let
  // the theme do its own thing with real numbers.
  //
  // previousVariant is deliberately null: ProductGallery skips the move when
  // the previous featured media id matches the new one, which it would here
  // since the variant hasn't actually changed — null makes the comparison
  // fail and the move run.
  var syntheticVariantEvent = false;
  function resyncGalleryToVariant() {
    if (!form || !currentVariant || !currentVariant.featured_media) return;
    // Force the layout that un-hiding the gallery invalidated before firing,
    // since the theme's handler measures scrollWidth/clientWidth to decide
    // which of its two paths to take. Synchronous for the same reason as
    // bringViewerIntoView — a rAF here can simply never run.
    gallery.getBoundingClientRect();
    syntheticVariantEvent = true;
    try {
      form.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant: currentVariant, previousVariant: null },
      }));
    } finally {
      syntheticVariantEvent = false;
    }
  }

  // ---- mode toggle --------------------------------------------------------

  function setToggleButtonsState() {
    var is3d = mode === '3d';
    if (belowSelectorBtn) {
      belowSelectorBtn.setAttribute('aria-pressed', String(is3d));
      var singleLabel = belowSelectorBtn.querySelector('[data-viewer-toggle-single-label]');
      var singleIcon3d = belowSelectorBtn.querySelector('[data-viewer-toggle-single-icon-3d]');
      var singleIconPhoto = belowSelectorBtn.querySelector('[data-viewer-toggle-single-icon-photo]');
      // Glyph and label name the mode a click would switch TO, not the
      // current one — cube + "3D" while looking at photos, camera +
      // "Fotos" while looking at the 3D view.
      if (singleLabel) singleLabel.textContent = is3d ? 'Fotos' : '3D';
      if (singleIcon3d) singleIcon3d.hidden = is3d;
      if (singleIconPhoto) singleIconPhoto.hidden = !is3d;
    }
    if (mobileToggleBtn) {
      mobileToggleBtn.setAttribute('aria-pressed', String(is3d));
      var photoIcon = mobileToggleBtn.querySelector('[data-viewer-toggle-icon-photo]');
      var threeDIcon = mobileToggleBtn.querySelector('[data-viewer-toggle-icon-3d]');
      var mobileLabel = mobileToggleBtn.querySelector('[data-viewer-toggle-label]');
      // Same destination convention as the below-selector button.
      if (photoIcon) photoIcon.hidden = !is3d;
      if (threeDIcon) threeDIcon.hidden = is3d;
      if (mobileLabel) mobileLabel.textContent = is3d ? 'Fotos ansehen' : '3D ansehen';
    }
    // Maße/AR only make sense once the 3D view is actually showing.
    if (dimsToggleBtn) dimsToggleBtn.hidden = !is3d;
    if (arStartBtn) arStartBtn.hidden = !is3d;
  }

  // `source` names what triggered the switch, and is reported with the open
  // event so the two entry points (the button under the swatches, the
  // repurposed gallery zoom bubble) and the QR deep link can be told apart.
  function setMode(next, source) {
    var was = mode;
    mode = next;
    var showViewer = mode === '3d';

    Array.from(gallery.children).forEach(function (child) {
      if (child === pane) return;
      if (child === mobileToggleBtn) return;
      if (child === qrModal) return;
      if (child.tagName === 'SCRIPT' || child.tagName === 'LINK') return;
      child.hidden = showViewer;
    });
    pane.hidden = !showViewer;

    setToggleButtonsState();

    if (showViewer) {
      core.mount();
      bringViewerIntoView();
      // Gated on an actual photo->3D transition: setMode('3d') is also
      // reachable while already in 3D (the QR deep link fires it on a page
      // that a previous visit left in 3D), and that isn't a new session.
      if (was !== '3d') {
        core.beginSession();
        track('b01_3d_open', { source: source || 'unknown', color: currentColorName || '', context: 'product' });
      }
    } else {
      core.closeDims();
      resyncGalleryToVariant();
      if (was === '3d') {
        // dwell_ms plus did_interact are the pair that separates "clicked 3D"
        // from "actually turned the chair" — the whole point of the metric.
        var session = core.endSession();
        track('b01_3d_close', {
          dwell_ms: session.dwellMs,
          did_interact: session.didInteract,
          color: currentColorName || '',
          context: 'product',
        });
      }
    }
  }

  // Clicking a photo part-way down the gallery (or the variant re-sync
  // above) can leave the page scrolled well past the top of the media
  // column. Switching to 3D then swaps a tall photo grid for a single
  // square canvas that sits back up at the top, so the viewer ends up
  // off-screen above and you're left looking at empty space. Only scrolls
  // when the pane really isn't on screen — if you're already looking at it,
  // moving the page under you would be worse than doing nothing.
  // Runs synchronously rather than in a rAF: getBoundingClientRect already
  // forces the layout that un-hiding the pane invalidated, so the numbers
  // here are current, and a rAF only adds a frame in which the callback can
  // be throttled away (background tabs drop them entirely).
  function bringViewerIntoView() {
    var r = pane.getBoundingClientRect();
    if (!r.height) return;
    var visible = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
    if (visible >= r.height * 0.6) return;
    pane.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  if (belowSelectorBtn) {
    belowSelectorBtn.addEventListener('click', function () {
      setMode(mode === '3d' ? 'photo' : '3d', 'below_selector');
    });
  }

  if (mobileToggleBtn) {
    mobileToggleBtn.addEventListener('click', function () {
      setMode(mode === '3d' ? 'photo' : '3d', 'gallery_bubble');
    });
  }

  setToggleButtonsState();

  // ---- color, driven by Shopify's variant:change -------------------------

  if (form) {
    form.addEventListener('variant:change', function (event) {
      var variant = event.detail && event.detail.variant;
      var colorName = variant && variant.option1;
      if (!colorName) return;
      // Our own re-fire carries no new color — it exists purely to nudge the
      // gallery — so don't redo the material work for it.
      if (syntheticVariantEvent) return;
      currentVariant = variant;
      currentColorName = colorName;
      // Only tracked while the 3D pane is up: a swatch click with photos
      // showing is ordinary variant selection, already covered by Shopify's
      // own events, and counting it here would drown the configurator signal.
      core.setColor(colorName, { track: mode === '3d' });
    });
  }

  // Last, as before the split: the deep link may call setMode, which needs
  // everything above in place.
  core.handleDeepLink();
})();
